import { getDefaultStore } from 'jotai';

import { getExternalLaunchToken } from '@/atoms/externalLaunchAuthAtoms';
import { IS_AUTH_SOURCE_OF_TRUTH_ENABLED } from '@/features/flags';
import { store } from '@/store';
import { isTauri } from '@/tauri/utils';
import { readProviderCookie } from '@/utils/auth/authProviderCookie';
import { AuthProvider } from '@/utils/auth/persistedAuthProvider';
import { isRecoveryEligible } from '@/utils/http/auth401RecoveryEligibility';
import { logger } from '@/utils/logger/logger';
import { AuthRetryRevisionStaleError } from '@heidi/api/errors';
import { isCacheReadyProvider } from '@heidi/auth/predicates';
import { authCacheSuppressedAtom, readAuthRevision } from '@heidi/auth/state';

/**
 * State reads shared between this async helper and the sync variant
 * (`getAuthTokenForHttpClientSync`). Exported so the sync helper's module —
 * which must not take on its own `@/store` dependency — reaches the store
 * through this already-store-coupled module instead of importing it
 * directly. Keeping both helpers' state-touching branches routed through
 * these three functions also guarantees they can't drift out of shape
 * relative to each other.
 *
 * @internal These are raw state reads and deliberately do NOT consult the
 * suppression atom (`authCacheSuppressedAtom`) — suppression is enforced in
 * the two wrapper helpers (`getAuthTokenForHttpClient` /
 * `getAuthTokenForHttpClientSync`), which are the only intended consumers.
 * A new caller reaching for a token read must go through one of the
 * wrappers, never these directly, or it will attach a bearer token even
 * inside the suppressed public-embed context.
 */
export function getExternalLaunchAuthToken(): string | null | undefined {
  return getExternalLaunchToken();
}

export function getReduxAuthProvider(): AuthProvider | undefined {
  return store.getState().auth.authProvider;
}

export function getLegacyReduxToken(): string | null | undefined {
  return store.select.auth.getToken(store.getState());
}

/**
 * Shared token read for cache-authed axios request interceptors. Used by
 * globalHTTP's authenticated instance and scribeHTTP.
 *
 * Branches in order:
 *
 * 0. **Suppression short-circuit.** When the auth cache is suppressed (the
 *    public chat embed), attach no bearer at all — not an external-launch
 *    token from a prior FHIR/MD launch, and not a redux-persist-rehydrated
 *    `auth.token`. The embed's contract is anonymous/public, and every
 *    interceptor consuming this helper guards the empty case
 *    (`token ? Bearer : ''`), so `undefined` yields no Authorization header.
 *
 * 1. **External-launch token presence gate (flag-agnostic).** When
 *    `externalLaunchTokenAtom` is non-null, return it directly.
 *    Covers FHIR (writers populate it at EHR launch) AND Medical
 *    Director (useMdAuthPersistence's restore + bridge populate it
 *    on widget reopen / initial Kinde login). Both flows authenticate
 *    outside the cache's source-of-truth abstractions.
 *
 * 2. **INTERIM FHIR fallback.** When external is empty AND
 *    `state.auth.authProvider === AuthProvider.FHIR`, read `auth.token`
 *    directly. Handles the post-reload case where the external-launch token
 *    (never persisted) is empty but `auth.token` (persisted)
 *    has rehydrated the FHIR JWT. Without this the interceptor would fall
 *    through to the cache, whose FHIR branch returns null, strip the
 *    Bearer, and 401 every authed FHIR request. Interim: goes away once
 *    FHIR consumers stop depending on `auth.token`.
 *
 * 3. **Flag OFF → legacy Redux read.**
 *
 * 4. **Flag ON → cache-aware async getAccessToken, gated on whether the
 *    effective provider + platform is in CACHE_READY_PROVIDERS (AUTH0 web
 *    currently). Non-allowlisted providers (KINDE, FHIR, etc.) fall
 *    through to the legacy Redux read even with the flag ON.
 *    Three dual-run fallbacks handle the rollout window: (a) boot window
 *    (cache still loading → serve Redux token), (b) transient null
 *    (session endpoint blip → serve Redux token), (c) dynamic import
 *    failure (ChunkLoadError → serve Redux token). Both lazy imports break
 *    the static cycle through the Redux store init chain; they resolve
 *    once and cache at steady state.
 */
export type AuthTokenContext = {
  token: string | null;
  /**
   * The ACTUAL effective provider at dispatch — always carried, never
   * erased to `''` to signal ineligibility, a dropped context, or
   * suppression. Those are `eligible`/`stale` below. See the field doc on
   * `__authRecoveryContext` in `@heidi/api/client/types` for why a
   * conflated empty provider was a bug, not a convenience.
   */
  provider: string;
  authRevision: number;
  /** Whether `provider` was recovery-eligible at dispatch (`isRecoveryEligible`). */
  eligible: boolean;
  /**
   * `true` when this resolution itself detected a mid-operation identity
   * mismatch (see {@link acceptOrDropContext}) — the token and the revision
   * it's paired with span two different moments. Always treat a stale
   * context as non-current regardless of what a later live-identity
   * comparison shows; the revision comparison itself isn't trustworthy here.
   */
  stale: boolean;
};

/**
 * Same branches as {@link getAuthTokenForHttpClient}, additionally returning
 * the provider + live `authRevision` the token was resolved under, and
 * whether that provider was recovery-eligible / the resolution was already
 * known-stale at capture time. The 401 handler branches on `eligible` (not
 * on `provider` presence) to decide whether a request gets legacy handling
 * unchanged or the recovery treatment — see `scribeHttpErrorHandler.ts`.
 *
 * A post-await re-read alone is not sufficient to make this atomic: the
 * token itself may be from work started under an earlier session (e.g. a
 * force-refresh that began before a provider switch, completed after).
 * Every branch that crosses an await captures {@link preProvider}/{@link
 * currentAuthRevision} BEFORE it, then only accepts the result when the
 * provider is unchanged across the whole operation AND the revision the
 * token was committed under still matches a fresh post-await read — see
 * {@link acceptOrDropContext}. A mismatch on either axis keeps the token
 * (still safe as a bearer) but stamps `stale: true` rather than returning a
 * silently mixed-time tuple.
 */
// Read fresh, synchronously, in the same continuation as each branch's
// return — never once up front. A single early read would pair a
// pre-await revision with a post-await token, breaking the atomic
// {token, authRevision} contract the 401 handler relies on.
function currentAuthRevision(): number {
  return readAuthRevision();
}

async function resolveAuthTokenContext(): Promise<AuthTokenContext | null> {
  if (getDefaultStore().get(authCacheSuppressedAtom)) {
    return null;
  }
  const externalToken = getExternalLaunchAuthToken();
  if (externalToken) {
    return {
      token: externalToken,
      provider: getReduxAuthProvider() ?? '',
      authRevision: currentAuthRevision(),
      eligible: false,
      stale: false,
    };
  }
  const reduxProvider = getReduxAuthProvider();
  if (reduxProvider === AuthProvider.FHIR) {
    return {
      token: getLegacyReduxToken() ?? null,
      provider: AuthProvider.FHIR,
      authRevision: currentAuthRevision(),
      eligible: false,
      stale: false,
    };
  }
  if (IS_AUTH_SOURCE_OF_TRUTH_ENABLED()) {
    try {
      // The ONE shared auth-identity snapshot (also used by the auth-cache's
      // own commit/acceptance gates and the axios destructive-exit fence) —
      // never Redux `authProvider` directly. Lazy: `@/lib/auth/authIdentitySnapshot`
      // reaches `@/store`, which transitively reaches back to this module
      // through the HTTP-client wiring — see the module doc comment there.
      const { getCurrentAuthIdentitySnapshot } =
        await import('@/lib/auth/authIdentitySnapshot');
      const { provider: preProvider, authRevision: preRevision } =
        getCurrentAuthIdentitySnapshot();
      const tauri = isTauri();

      // Accept the token ONLY when (a) the provider read here matches the
      // provider read fresh right now (unchanged across the whole
      // operation), and (b) the revision the token was committed under
      // still equals a fresh live read (nothing else landed since). A
      // mismatch on either axis means a mixed-time tuple risk (provider
      // switch, revision-switch, or an ABA session replacement during the
      // operation) — keep the token (still safe as a bearer) but stamp
      // `stale: true` rather than silently pairing values from two moments.
      // `provider` is always `preProvider` — the actual dispatch provider,
      // never erased — regardless of which branch fires below.
      const acceptOrDropContext = (
        token: string | null,
        committedRevision: number
      ): AuthTokenContext => {
        const { provider: postProvider, authRevision: postRevision } =
          getCurrentAuthIdentitySnapshot();
        const providerCompatible = postProvider === preProvider;
        const revisionConsistent = committedRevision === postRevision;
        const eligible = isRecoveryEligible(preProvider as AuthProvider, tauri);
        if (!providerCompatible || !revisionConsistent) {
          return {
            token,
            provider: preProvider,
            authRevision: postRevision,
            eligible,
            stale: true,
          };
        }
        return {
          token,
          provider: preProvider,
          authRevision: postRevision,
          eligible,
          stale: false,
        };
      };

      if (isCacheReadyProvider(preProvider as AuthProvider, tauri)) {
        const { authStateAtom, authTransientFailureAtom, getAccessTokenWithRevision } =
          await import('@/lib/auth');
        const jotaiStore = getDefaultStore();

        const cacheState = jotaiStore.get(authStateAtom);
        const reduxToken = getLegacyReduxToken();
        if (cacheState === 'loading' && reduxToken != null) {
          return acceptOrDropContext(reduxToken, preRevision);
        }

        let outcome = await getAccessTokenWithRevision();

        if (outcome.status === 'stale') {
          // Not-yet-dispatched request: the operation-level commit gate
          // tripped because live identity moved while this fetch was in
          // flight. Unlike a pinned retry, a pre-dispatch request carries no
          // prior identity to defend — it can simply re-resolve under
          // whatever is live now, exactly as if it had been issued a moment
          // later. Re-resolve ONCE rather than mapping straight to a null
          // bearer (which would dispatch with an empty Authorization header,
          // reach the backend as a headered 401, and — now that the captured
          // provider is never erased — hit the eligible branch's legacy
          // logout against a session that's actually still alive).
          outcome = await getAccessTokenWithRevision();
          if (outcome.status === 'stale') {
            // Identity is still moving. Cancel pre-dispatch, non-destructively,
            // with the same sentinel the retry-pin path already uses — never a
            // second silent re-resolve, never an empty bearer.
            const postIdentity = getCurrentAuthIdentitySnapshot();
            logger.info(
              '[auth-cache] pre-dispatch cancellation - identity moved twice while resolving',
              {
                preProvider,
                preRevision,
                postProvider: postIdentity.provider,
                postRevision: postIdentity.authRevision,
              }
            );
            throw new AuthRetryRevisionStaleError();
          }
        }

        if (outcome.status !== 'success') {
          if (jotaiStore.get(authTransientFailureAtom)) {
            return acceptOrDropContext(reduxToken ?? null, preRevision);
          }
          return {
            token: null,
            provider: preProvider,
            authRevision: currentAuthRevision(),
            eligible: isRecoveryEligible(preProvider as AuthProvider, tauri),
            stale: false,
          };
        }

        // outcome.status === 'success' — outcome.authRevision is the
        // revision the token was COMMITTED under; validate it against a
        // fresh post-await read before stamping a recovery context.
        return acceptOrDropContext(outcome.token, outcome.authRevision);
      }
      // Cache-ready gate failed — same eligibility rules still apply to the
      // legacy Redux read below (this is desktop's normal path today).
      // Desktop recovery depends on this Redux read seeing a rotated token:
      // the ONLY thing that writes a refreshed token into Redux (and
      // advances authRevision on that rotation) is the IS_DUAL_RUN_ACTIVE
      // mirror in getAccessToken.ts's applySuccessOutcome. If that mirror
      // is ever removed, a desktop forced-refresh retry re-sends the SAME
      // dead token here — the exact regression this feature was built to fix.
      return acceptOrDropContext(getLegacyReduxToken() ?? null, preRevision);
    } catch (err) {
      if (err instanceof AuthRetryRevisionStaleError) throw err;
      logger.warn(
        '[auth-cache] getAuthToken flag-ON path failed, falling back to Redux token',
        {
          error: err instanceof Error ? err.message : String(err),
        }
      );
      const reduxProvider = getReduxAuthProvider();
      const revision = currentAuthRevision();
      const tauri = isTauri();

      // Desktop never has a provider cookie — Redux is authoritative, same
      // as the primary path. Preserving eligibility here (not hardcoding
      // `false`) matters: AUTH0_DESKTOP with both flags on must stay
      // recovery-eligible even when this fallback fires, or a desktop
      // 401 during a snapshot-import failure would wrongly fall through
      // to the legacy handler's unconditional logout.
      if (tauri) {
        return {
          token: getLegacyReduxToken() ?? null,
          provider: reduxProvider ?? '',
          authRevision: revision,
          eligible: isRecoveryEligible(reduxProvider, tauri),
          stale: false,
        };
      }

      // Web: reconcile the cookie against Redux exactly once. A read
      // failure or a genuine disagreement between the two means this
      // fallback has no trustworthy answer for "which provider is this
      // request under" — mapping that uncertainty to `eligible: false`
      // would route it to the legacy handler, which logs out unconditionally
      // on any definitive signal. Cancelling before dispatch (like the
      // pre-dispatch cancellation above) is the safe response: the request
      // is abandoned non-destructively and a later request re-resolves.
      let cookieProvider: AuthProvider | null;
      try {
        cookieProvider = readProviderCookie();
      } catch {
        logger.info(
          '[auth-cache] fallback identity uncertain - cookie read threw, cancelling before dispatch'
        );
        throw new AuthRetryRevisionStaleError();
      }

      if (cookieProvider !== null && cookieProvider !== reduxProvider) {
        logger.info(
          '[auth-cache] fallback identity uncertain - cookie/Redux provider mismatch, cancelling before dispatch'
        );
        throw new AuthRetryRevisionStaleError();
      }

      const provider = cookieProvider ?? reduxProvider;
      return {
        token: getLegacyReduxToken() ?? null,
        provider: provider ?? '',
        authRevision: revision,
        eligible: isRecoveryEligible(provider, tauri),
        stale: false,
      };
    }
  }
  // Flag off — no recovery of any kind, same as the pre-recovery behavior.
  return {
    token: getLegacyReduxToken() ?? null,
    provider: getReduxAuthProvider() ?? '',
    authRevision: currentAuthRevision(),
    eligible: false,
    stale: false,
  };
}

export async function getAuthTokenForHttpClient(): Promise<string | null | undefined> {
  const context = await resolveAuthTokenContext();
  return context?.token;
}

/** Token + the recovery context it was resolved under. See {@link resolveAuthTokenContext}. */
export async function getAuthContextForHttpClient(): Promise<AuthTokenContext | null> {
  return resolveAuthTokenContext();
}
